import { useCallback, useEffect, useMemo, useState } from 'react';

import moment from 'moment';
import momentTz from 'moment-timezone';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowBendDoubleUpRight,
  ArrowDownLeft,
  ArrowURightDown,
  Bank,
  Calendar,
  Coins,
  CreditCard,
  Flag,
  Money,
} from 'phosphor-react';
import { Tooltip as ReactTooltip } from 'react-tooltip';

import { DillTransaction, formatMoney } from '@dill/dill-shared';
import { useAppDispatch, useAppSelector } from '../../../../../redux/store';
import { AppPaidInvoicesModal } from '../../../../general/AppPaidInvoicesModal/AppPaidInvoicesModal';
import { AppExternalPaymentDetailsModal } from '../../../../general/AppExternalPaymentDetailsModal/AppExternalPaymentDetailsModal';
import {
  getSupplierTransactions,
  getTransactionById,
} from '../../../../../redux/services/paymentsService';
import AppTransactionStatusPill from '../../../../general/AppTransactionStatusPill/AppTransactionStatusPill';

import AppDateRangePickerModal from '../../../../general/AppDateRangePickerModal/AppDateRangePickerModal';
import { ArrowDownCircle, ArrowUpCircle } from 'react-feather';
import { DillButton, DillCheckbox, DillDropDownFilter, DillTable } from '../../../../general/Dill';
import type { DillColumn } from '../../../../general/Dill';
import { COLORS } from '../../../../../utils/colors';
import { buildSupplierPath } from '../../../../../utils/helpers';

interface SupplierCustomerPaymentsTableProps {
  buyerId: string;
  selectedJobIds?: string[];
}

export const SupplierCustomerPaymentsTable = ({
  buyerId,
  selectedJobIds,
}: SupplierCustomerPaymentsTableProps) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const [openPaidInvoiceDetails, setOpenPaidInvoiceDetails] = useState(false);
  const { supplierTransactions } = useAppSelector((state) => state.payments);
  const [isAppDateRangePickerModalOpen, setIsAppDateRangePickerModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<DillTransaction | null>(null);
  const [dateRange, setDateRange] = useState<{
    startDate?: Date;
    endDate?: Date;
  }>({
    startDate: moment(new Date()).subtract(3, 'months').toDate(),
    endDate: new Date(),
  });
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const validTimezone = momentTz.tz.zone(timezone) ? timezone : 'America/Los_Angeles';
  const [searchText, setSearchText] = useState('');

  const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: string[] } | null>(null);

  const [sortOptions, setSortOptions] = useState<{ sortOrder: 'asc' | 'desc'; sortBy: string }>({
    sortOrder: 'desc',
    sortBy: 'createdAt',
  });
  const [hoveredPayoutId, setHoveredPayoutId] = useState('');
  const [selectedExternalPayment, setSelectedExternalPayment] = useState<DillTransaction | null>(
    null
  );

  const handleShowPaidInvoices = useCallback(
    async (payment: DillTransaction) => {
      // Payments made outside of Dill have no Dill receipt/refund details to load.
      if (payment.isExternalPayment) {
        setSelectedExternalPayment(payment);
        return;
      }
      const transactionResults = await dispatch(getTransactionById({ transactionId: payment.id }));
      let finalTransaction = payment;
      if (transactionResults.type === 'payments/getTransactionById/fulfilled') {
        const res = transactionResults?.payload as any;
        finalTransaction = res.data as DillTransaction;
      }
      setSelectedTransaction(finalTransaction);
      setOpenPaidInvoiceDetails(true);
      const params = new URLSearchParams(location.search);
      params.set('tab', 'PAYMENTS');
      params.set('transactionId', payment.id);
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    },
    [dispatch, location]
  );

  const columns: DillColumn<DillTransaction>[] = useMemo(
    () => [
      {
        key: 'name',
        label: '',
        width: 180,
        sortable: false,
        render: (_, row) => {
          const methodType =
            row.type === 'DILL_FACILITATION_FEE_PAYMENT_BUYER' ||
            row.type === 'DILL_FACILITATION_FEE_PAYMENT_SUPPLIER'
              ? 'FEES'
              : row.type;
          const buyerName = row?.buyer?.name.toLocaleLowerCase() ?? '';
          const displayName =
            row.type === 'DILL_FACILITATION_FEE_PAYMENT_BUYER' ||
            row.type === 'DILL_FACILITATION_FEE_PAYMENT_SUPPLIER'
              ? 'Dill'
              : row.type === 'PAYMENT' ||
                row.type === 'CREDIT_PAYMENT' ||
                row.type === 'PAYMENT_REQUEST_PAYMENT' ||
                row.type === 'FIXED_AMOUNT_PAYMENT'
              ? currentSupplier?.integrationType !== 'RUTTER_QBD'
                ? `${row?.buyer?.name.toLocaleLowerCase()} - ${row?.buyer?.quickbooksCustomerId}`
                : buyerName
              : row.type === 'WILL_CALL_PAYMENT' && row.buyer
              ? currentSupplier?.integrationType !== 'RUTTER_QBD'
                ? `${row?.buyer?.name.toLocaleLowerCase()} - ${row?.buyer?.quickbooksCustomerId}`
                : buyerName
              : row?.recipient?.name ?? '';

          return (
            <div className="w-full flex items-center pr-1">
              <div className="mx-0.5">
                {row.type === 'WITHDRAW' ? (
                  <div className="aspect-square rounded-full WARNING_100-BG p-2">
                    <ArrowUpCircle color={COLORS.WARNING_500} />
                  </div>
                ) : (
                  <div className="aspect-square rounded-full SUCCESS_100-BG p-2">
                    <ArrowDownCircle color={COLORS.SUCCESS_500} />
                  </div>
                )}
              </div>
              <div className="w-full">
                <div className="text-xs font-semibold w-full word-wrap capitalize">
                  {displayName ? displayName.toLowerCase() : displayName}
                </div>
                <div className="flex items-center mt-2 ">
                  {methodType === 'PAYMENT' ||
                  methodType === 'WILL_CALL_PAYMENT' ||
                  methodType === 'PAYMENT_REQUEST_PAYMENT' ||
                  methodType === 'CREDIT_PAYMENT' ||
                  methodType === 'FIXED_AMOUNT_PAYMENT' ? (
                    <ArrowDownLeft color={COLORS.SUCCESS_500} size={10} />
                  ) : methodType === 'FEES' ? (
                    <ArrowURightDown color={COLORS.WARNING_500} size={10} />
                  ) : (
                    <ArrowDownLeft color={COLORS.ERROR_500} size={10} className="rotate-180" />
                  )}
                  <div className="text-2xs ml-1">
                    {`${methodType === 'WITHDRAW' ? 'PAYOUT' : 'PAYMENT'}`}
                  </div>
                  {row.isExternalPayment && (
                    <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-semibold leading-none whitespace-nowrap">
                      Outside Dill
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        key: 'amount',
        label: 'Amount',
        sortable: true,
        numeric: true,
        width: 120,
        getSortValue: (row) => {
          const methodType =
            row.type === 'DILL_FACILITATION_FEE_PAYMENT_BUYER' ||
            row.type === 'DILL_FACILITATION_FEE_PAYMENT_SUPPLIER'
              ? 'FEES'
              : row.type;
          const total = row.totalAmountPaid;

          if (
            methodType === 'PAYMENT' ||
            methodType === 'WILL_CALL_PAYMENT' ||
            methodType === 'PAYMENT_REQUEST_PAYMENT' ||
            methodType === 'CREDIT_PAYMENT' ||
            methodType === 'FIXED_AMOUNT_PAYMENT'
          ) {
            if (row?.transactionErrorMessage) {
              return total;
            }
            if (row?.cardPaymentProvider === 'FISERV') {
              return total;
            }
            return total - (row?.facilitationFee ?? 0);
          } else {
            if (row?.cardPaymentProvider === 'FISERV' && total < 0) {
              return Math.abs(total);
            }
            return -total;
          }
        },
        render: (_, row) => {
          const methodType =
            row.type === 'DILL_FACILITATION_FEE_PAYMENT_BUYER' ||
            row.type === 'DILL_FACILITATION_FEE_PAYMENT_SUPPLIER'
              ? 'FEES'
              : row.type;
          const total = row.totalAmountPaid;
          let value = '';

          if (
            methodType === 'PAYMENT' ||
            methodType === 'WILL_CALL_PAYMENT' ||
            methodType === 'PAYMENT_REQUEST_PAYMENT' ||
            methodType === 'CREDIT_PAYMENT' ||
            methodType === 'FIXED_AMOUNT_PAYMENT'
          ) {
            if (row?.transactionErrorMessage) {
              value = `+${formatMoney(total, 2)}`;
            } else {
              if (row?.cardPaymentProvider === 'FISERV') {
                value = `+${formatMoney(total, 2)}`;
              } else {
                value = `+${formatMoney(total - (row?.facilitationFee ?? 0), 2)}`;
              }
            }
          } else {
            value = `-${formatMoney(total, 2)}`;
            if (total > 0) {
              value = `-${formatMoney(total, 2)}`;
            } else {
              value = `${formatMoney(Math.abs(total), 2)}`;
            }
          }

          return (
            <div className="flex flex-col">
              <p className={'text-xs text-left w-full'}>{value}</p>

              {row?.withdrawTransactionId && (
                <p
                  className="text-2xs text-left w-fit px-1.5 py-1 bg-green-500 mt-2 rounded-lg text-white cursor-pointer"
                  onMouseEnter={() => {
                    setHoveredPayoutId(row?.withdrawTransactionId || '');
                  }}
                  onMouseLeave={() => {
                    setHoveredPayoutId('');
                  }}>
                  Paid Out
                </p>
              )}
              {row.partialPaymentReason && (
                <p className="text-2xs text-left w-fit py-1 mt-0.5 rounded-lg text-white cursor-pointer">
                  <Flag
                    size={14}
                    weight="fill"
                    className="text-red-400"
                    data-tooltip-id={`dispute-flag-amount`}
                  />
                  <ReactTooltip
                    id={`dispute-flag-amount`}
                    place="right"
                    variant="dark"
                    opacity="100%"
                    style={{
                      padding: '5px',
                      background: '#222',
                      width: '100px',
                      zIndex: 99999,
                    }}>
                    <p className="text-2xs">Short payment</p>
                  </ReactTooltip>
                </p>
              )}
            </div>
          );
        },
      },
      {
        key: 'createdAt',
        label: 'Date',
        sortable: true,
        width: 120,
        getSortValue: (row) => (row.createdAt ? new Date(row.createdAt).getTime() : 0),
        render: (value) => (
          <p className={'text-xs text-left w-full'}>
            {value ? moment(value as string).format('MM/DD/YYYY') : ''}
          </p>
        ),
      },
      {
        key: 'processedDate',
        label: <span className="flex text-nowrap">Date Processed</span>,
        sortable: true,
        width: 140,
        getSortValue: (row) => (row.processedDate ? new Date(row.processedDate).getTime() : 0),
        render: (value) => (
          <p className={'text-xs text-left w-full'}>
            {value ? moment(value as string).format('MM/DD/YYYY') : ''}
          </p>
        ),
      },
      {
        key: 'transactionStatus',
        label: <span className="flex text-nowrap">Transaction Status</span>,
        sortable: true,
        getSortValue: (row) => row.transactionStatus || '',
        render: (_, row) => (
          <div className="w-full mr-6">
            <AppTransactionStatusPill status={row.transactionStatus} />
          </div>
        ),
      },
      {
        key: 'reconciliationStatus',
        label: <span className="flex text-nowrap">Reconciliation Status</span>,
        sortable: true,
        getSortValue: (row) => row.reconciliationStatus || '',
        render: (_, row) => (
          <div className="w-full mr-6">
            <AppTransactionStatusPill status={row.reconciliationStatus} />
          </div>
        ),
      },
      {
        key: 'method',
        label: 'Method',
        sortable: false,
        width: 150,
        render: (_, row) => {
          const methodType =
            row.type === 'DILL_FACILITATION_FEE_PAYMENT_BUYER' ||
            row.type === 'DILL_FACILITATION_FEE_PAYMENT_SUPPLIER'
              ? 'FEES'
              : row.type;
          if (row.isExternalPayment) {
            return (
              <div className="w-full flex">
                <div className="payment-type-logo">
                  <Money size={24} style={{ display: 'flex', alignSelf: 'center' }} />
                </div>
                <div>
                  <div className="text-xs font-bold">Paid outside Dill</div>
                  {row.externalReferenceNumber && (
                    <div className="payment-method-details">Ref #{row.externalReferenceNumber}</div>
                  )}
                </div>
              </div>
            );
          }
          return (
            <div>
              {methodType !== 'FEES' && (
                <div className="w-full flex">
                  <div className="payment-type-logo">
                    {row?.paymentMethod?.type === 'BANK_ACCOUNT' && row.type === 'WITHDRAW' ? (
                      <>
                        <ArrowBendDoubleUpRight
                          size={24}
                          style={{ display: 'flex', alignSelf: 'center' }}
                        />
                      </>
                    ) : row?.paymentMethod?.type === 'BANK_ACCOUNT' ? (
                      <Bank size={24} style={{ display: 'flex', alignSelf: 'center' }} />
                    ) : row.type === 'CREDIT_PAYMENT' ? (
                      <Coins size={24} style={{ display: 'flex', alignSelf: 'center' }} />
                    ) : row.type !== 'WITHDRAW' ? (
                      <CreditCard size={24} style={{ display: 'flex', alignSelf: 'center' }} />
                    ) : (
                      <></>
                    )}
                  </div>
                  <div>
                    {row?.paymentMethod?.type === 'BANK_ACCOUNT' && row.type === 'WITHDRAW' ? (
                      <div className="text-xs font-bold">Payout</div>
                    ) : row?.paymentMethod?.type === 'BANK_ACCOUNT' ? (
                      <div className="text-xs font-bold">Bank Transfer</div>
                    ) : row.type === 'CREDIT_PAYMENT' ? (
                      <div className="text-xs font-bold">Credits</div>
                    ) : row?.cardType ? (
                      <div className=" text-xs font-bold">
                        {row?.cardType === 'CREDIT' ? 'Credit Card' : 'Debit / Other Card'}
                      </div>
                    ) : row?.paymentMethod?.cardType ? (
                      <div className=" text-xs font-bold">
                        {row?.paymentMethod?.cardType === 'CREDIT'
                          ? 'Credit Card'
                          : 'Debit / Other Card'}
                      </div>
                    ) : row.type !== 'WITHDRAW' ? (
                      <div className=" text-xs font-bold">Credit/Debit Card</div>
                    ) : (
                      <></>
                    )}
                    <div className="payment-method-details">{`${
                      row.type === 'PAYMENT_REQUEST_PAYMENT' || row.type === 'WILL_CALL_PAYMENT'
                        ? row?.paymentMethodName ?? ''
                        : row?.paymentMethod?.paymentMethodName ?? ''
                    }`}</div>
                  </div>
                </div>
              )}
            </div>
          );
        },
      },
      {
        key: 'viewDetails',
        label: '',
        sortable: false,
        width: 120,
        render: (_, row) => {
          const methodType =
            row.type === 'DILL_FACILITATION_FEE_PAYMENT_BUYER' ||
            row.type === 'DILL_FACILITATION_FEE_PAYMENT_SUPPLIER'
              ? 'FEES'
              : row.type;
          return (
            <div className="flex ml-1">
              {methodType !== 'FEES' && (
                <DillButton
                  label="Details"
                  variant="secondary"
                  onClick={() => {
                    handleShowPaidInvoices(row);
                  }}
                />
              )}
            </div>
          );
        },
      },
    ],
    [currentSupplier?.integrationType]
  );

  useEffect(() => {
    if (
      location.pathname.startsWith(buildSupplierPath(currentSupplier?.id, '/customers')) &&
      !openPaidInvoiceDetails
    ) {
      const params = new URLSearchParams(location.search);
      const transactionId = params.get('transactionId');
      if (transactionId) {
        const transaction = supplierTransactions.find((trans) => trans.id === transactionId);
        if (transaction) {
          handleShowPaidInvoices(transaction);
        }
      }
    }
  }, [location.pathname, supplierTransactions, location.search, openPaidInvoiceDetails]);

  const handleViewMainPayment = (val: string) => {
    const transaction = supplierTransactions.find((trans) => trans.id === val);
    if (transaction) {
      handleShowPaidInvoices(transaction);
    }
  };

  useEffect(() => {
    if (dateRange && user) {
      dispatch(
        getSupplierTransactions({
          supplierId: currentSupplier?.id || '',
          startDate: dateRange?.startDate
            ? moment(dateRange?.startDate).format('MMM DD, YYYY')
            : '',
          endDate: dateRange?.endDate ? moment(dateRange?.endDate).format('MMM DD, YYYY') : '',
          timezone: validTimezone,
          buyerId,
        })
      );
    }

    return () => {};
  }, [dateRange, user?.id, buyerId]);

  const paymentMethodTypesFilterOptions = [
    { label: 'Card Only', value: 'CARD_ONLY' },
    { label: 'Bank ACH Only', value: 'BANK_ACH_ONLY' },
  ];

  const otherFilterOptions = [
    { label: 'Payouts', value: 'PAYOUTS' },
    { label: 'Credits', value: 'CREDITS' },
    { label: 'Payments', value: 'PAYMENTS' },
    { label: 'Payments Paid Out', value: 'PAYMENTS_PAID_OUT' },
    { label: 'Payments NOT Paid Out', value: 'PAYMENTS_NOT_PAID_OUT' },
    { label: 'Reconciliation Processed', value: 'RECONCILIATION_PROCESSED' },
    { label: 'Reconciliation Pending', value: 'RECONCILIATION_PENDING' },
    { label: 'Reconciliation Failed', value: 'RECONCILIATION_FAILED' },
    { label: 'Reconciliation Waiting', value: 'RECONCILIATION_WAITING' },
    { label: 'Transaction Processed', value: 'TRANSACTION_PROCESSED' },
    { label: 'Transaction Pending', value: 'TRANSACTION_PENDING' },
    { label: 'Transaction Failed', value: 'TRANSACTION_FAILED' },
    { label: 'Short Payments', value: 'PARTIAL_PAYMENTS' },
  ];

  const createCheckboxItem = ({
    label,
    value,
    isChecked,
  }: {
    label: string;
    value: string;
    isChecked: boolean;
  }) => ({
    label,
    value,
    labelInElement: (
      <div className="flex mx-1 my-1 items-center gap-2">
        <DillCheckbox isChecked={isChecked} />
        <div className="text-black text-xs">{label}</div>
      </div>
    ),
  });

  const filterItems = [
    {
      id: 'status',
      label: 'Payment methods',
      value: paymentMethodTypesFilterOptions.map((option) =>
        createCheckboxItem({
          label: option.label,
          value: option.value,
          isChecked: selectedFilters?.status?.includes(option.value) ?? false,
        })
      ),
    },
    {
      id: 'other',
      label: 'Other',
      value: otherFilterOptions.map((option) =>
        createCheckboxItem({
          label: option.label,
          value: option.value,
          isChecked: selectedFilters?.other?.includes(option.value) ?? false,
        })
      ),
    },
  ];

  const handleFilterChange = (data: Record<string, { value: string }[]>) => {
    const transformed = Object.fromEntries(
      Object.entries(data).map(([key, arr]) => [key, arr.map((item) => item.value)])
    );
    setSelectedFilters(transformed);
  };

  const supplierTransactionsFiltered = useMemo(() => {
    const activeJobIds = (selectedJobIds ?? []).filter((id) => id !== 'ALL');
    let finalFiltered =
      activeJobIds.length > 0
        ? supplierTransactions.filter((transaction) =>
            transaction.invoicePayments.some((ip) => ip.jobId && activeJobIds.includes(ip.jobId))
          )
        : supplierTransactions;
    if (supplierTransactions.length > 0 && (selectedFilters?.status || selectedFilters?.other)) {
      const searched = supplierTransactions.filter((transaction) => {
        let filters: {
          isMatchPaymentMethodTypeFilter?: boolean;
          isMatchOtherFilter?: boolean;
        } = {};

        if (selectedFilters?.status && selectedFilters?.status.length > 0) {
          if (
            transaction?.paymentType === 'BANK' &&
            selectedFilters?.status.includes('BANK_ACH_ONLY')
          ) {
            filters = {
              ...filters,
              isMatchPaymentMethodTypeFilter: true,
            };
          } else if (
            transaction?.paymentType === 'CARD' &&
            selectedFilters?.status.includes('CARD_ONLY')
          ) {
            filters = {
              ...filters,
              isMatchPaymentMethodTypeFilter: true,
            };
          } else {
            filters = {
              ...filters,
              isMatchPaymentMethodTypeFilter: false,
            };
          }
        }

        if (selectedFilters?.other && selectedFilters?.other.length > 0) {
          if (selectedFilters?.other.includes('PAYOUTS') && transaction?.type === 'WITHDRAW') {
            filters = {
              ...filters,
              isMatchOtherFilter: true,
            };
          } else if (
            selectedFilters?.other.includes('CREDITS') &&
            transaction?.type === 'CREDIT_PAYMENT'
          ) {
            filters = {
              ...filters,
              isMatchOtherFilter: true,
            };
          } else if (
            selectedFilters?.other.includes('PAYMENTS') &&
            transaction?.type !== 'CREDIT_PAYMENT' &&
            transaction?.type !== 'WITHDRAW'
          ) {
            filters = {
              ...filters,
              isMatchOtherFilter: true,
            };
          } else if (
            selectedFilters?.other.includes('PAYMENTS_PAID_OUT') &&
            transaction?.type !== 'CREDIT_PAYMENT' &&
            transaction?.type !== 'WITHDRAW' &&
            transaction?.withdrawTransactionId
          ) {
            filters = {
              ...filters,
              isMatchOtherFilter: true,
            };
          } else if (
            selectedFilters?.other.includes('PAYMENTS_NOT_PAID_OUT') &&
            !transaction?.isExternalPayment &&
            transaction?.type !== 'CREDIT_PAYMENT' &&
            transaction?.type !== 'WITHDRAW' &&
            !transaction?.withdrawTransactionId &&
            transaction.transactionStatus === 'processed'
          ) {
            filters = {
              ...filters,
              isMatchOtherFilter: true,
            };
          } else if (
            (selectedFilters?.other.includes('RECONCILIATION_PENDING') &&
              transaction?.reconciliationStatus === 'pending') ||
            (selectedFilters?.other.includes('RECONCILIATION_FAILED') &&
              transaction?.reconciliationStatus === 'failed') ||
            (selectedFilters?.other.includes('RECONCILIATION_PROCESSED') &&
              transaction?.reconciliationStatus === 'processed') ||
            (selectedFilters?.other.includes('RECONCILIATION_WAITING') &&
              transaction?.reconciliationStatus === 'waiting')
          ) {
            filters = {
              ...filters,
              isMatchOtherFilter: true,
            };
          } else if (
            (selectedFilters?.other.includes('TRANSACTION_PENDING') &&
              transaction?.transactionStatus === 'pending') ||
            (selectedFilters?.other.includes('TRANSACTION_FAILED') &&
              transaction?.transactionStatus === 'failed') ||
            (selectedFilters?.other.includes('TRANSACTION_PROCESSED') &&
              transaction?.transactionStatus === 'processed')
          ) {
            filters = {
              ...filters,
              isMatchOtherFilter: true,
            };
          } else if (
            selectedFilters?.other.includes('PARTIAL_PAYMENTS') &&
            transaction?.partialPaymentReason
          ) {
            filters = {
              ...filters,
              isMatchOtherFilter: true,
            };
          } else {
            filters = {
              ...filters,
              isMatchOtherFilter: false,
            };
          }
        }

        return Object.values(filters).every((item) => item);
      });

      finalFiltered = searched;
    }
    if (finalFiltered.length > 0) {
      finalFiltered = [...finalFiltered].sort((transactionA, transactionB) => {
        let sortFieldA = '';
        let sortFieldB = '';
        if (sortOptions.sortBy === 'name') {
          const buyerA = transactionA?.buyer;
          const buyerB = transactionB?.buyer;
          if (buyerA && buyerA?.name) {
            sortFieldA = buyerA?.name;
          }
          if (buyerB && buyerB?.name) {
            sortFieldB = buyerB.name;
          }
        } else if (sortOptions.sortBy === 'amount') {
          const getDisplayAmount = (transaction: DillTransaction) => {
            const methodType =
              transaction.type === 'DILL_FACILITATION_FEE_PAYMENT_BUYER' ||
              transaction.type === 'DILL_FACILITATION_FEE_PAYMENT_SUPPLIER'
                ? 'FEES'
                : transaction.type;
            const total = transaction.totalAmountPaid;

            if (
              methodType === 'PAYMENT' ||
              methodType === 'WILL_CALL_PAYMENT' ||
              methodType === 'PAYMENT_REQUEST_PAYMENT' ||
              methodType === 'CREDIT_PAYMENT' ||
              methodType === 'FIXED_AMOUNT_PAYMENT'
            ) {
              if (transaction?.transactionErrorMessage) {
                return total;
              }
              if (transaction?.cardPaymentProvider === 'FISERV') {
                return total;
              }
              return total - (transaction?.facilitationFee ?? 0);
            } else {
              if (transaction?.cardPaymentProvider === 'FISERV' && total < 0) {
                return Math.abs(total);
              }
              return -total;
            }
          };

          const amountA = getDisplayAmount(transactionA);
          const amountB = getDisplayAmount(transactionB);

          if (sortOptions.sortOrder === 'asc') {
            return amountA - amountB;
          } else {
            return amountB - amountA;
          }
        } else {
          const key = sortOptions.sortBy as keyof typeof transactionA;
          sortFieldA = (transactionA[key] || '') as string;
          sortFieldB = (transactionB[key] || '') as string;
        }
        if (sortFieldA > sortFieldB) {
          if (sortOptions.sortOrder === 'asc') {
            return 1;
          } else {
            return -1;
          }
        } else if (sortFieldA < sortFieldB) {
          if (sortOptions.sortOrder === 'asc') {
            return -1;
          } else {
            return 1;
          }
        } else {
          return 0;
        }
      });
    }

    if (searchText) {
      const searched = finalFiltered.filter((transaction) => {
        let totalAmount = `${transaction?.totalAmountPaid}`;
        if (
          transaction.type === 'PAYMENT' ||
          transaction.type === 'WILL_CALL_PAYMENT' ||
          transaction.type === 'PAYMENT_REQUEST_PAYMENT' ||
          transaction.type === 'CREDIT_PAYMENT' ||
          transaction.type === 'FIXED_AMOUNT_PAYMENT'
        ) {
          totalAmount = `+${formatMoney(
            transaction?.totalAmountPaid - (transaction?.facilitationFee ?? 0),
            2
          )}`;
        } else {
          totalAmount = `-${formatMoney(transaction?.totalAmountPaid, 2)}`;
        }

        const paymentMethodName =
          transaction.type === 'PAYMENT_REQUEST_PAYMENT' || transaction.type === 'WILL_CALL_PAYMENT'
            ? transaction?.paymentMethodName ?? ''
            : transaction?.paymentMethod?.paymentMethodName ?? '';

        if (
          (transaction.invoicePayments.length > 0 &&
            transaction.invoicePayments
              .map((inv) => inv.invoiceNumber.toLocaleLowerCase())
              .some((invNo: string) => invNo.includes(searchText.toLowerCase()))) ||
          (transaction?.buyer?.name &&
            transaction?.buyer?.name.toLowerCase().includes(searchText.toLowerCase())) ||
          (transaction?.buyer?.quickbooksCustomerId &&
            transaction?.buyer?.quickbooksCustomerId
              .toLowerCase()
              .includes(searchText.toLowerCase())) ||
          (transaction?.recipient?.name &&
            transaction?.recipient?.name.toLowerCase().includes(searchText.toLowerCase())) ||
          (paymentMethodName &&
            paymentMethodName.toLowerCase().includes(searchText.toLowerCase())) ||
          (totalAmount && totalAmount.toLowerCase().includes(searchText.toLowerCase())) ||
          (transaction.createdAt &&
            moment(transaction.createdAt)
              .format('MM/DD/YYYY')
              .toLowerCase()
              .includes(searchText.toLowerCase())) ||
          (transaction.processedDate &&
            moment(transaction.processedDate)
              .format('MM/DD/YYYY')
              .toLowerCase()
              .includes(searchText.toLowerCase()))
        ) {
          return true;
        }
        return false;
      });
      finalFiltered = searched;
    }

    return finalFiltered;
  }, [supplierTransactions, selectedFilters, sortOptions, searchText, selectedJobIds]);

  return (
    <div className=" h-full pt-4">
      <AppExternalPaymentDetailsModal
        transaction={selectedExternalPayment}
        accountType="supplier"
        onClose={() => setSelectedExternalPayment(null)}
      />
      <AppPaidInvoicesModal
        open={openPaidInvoiceDetails}
        handleClose={(isReloadAllTransactionsOnClose) => {
          setOpenPaidInvoiceDetails(false);
          // Clear transactionId from URL when closing the modal
          const params = new URLSearchParams(location.search);
          if (params.has('transactionId')) {
            navigate(location.pathname, { replace: true });
          }
          if (isReloadAllTransactionsOnClose) {
            dispatch(
              getSupplierTransactions({
                supplierId: currentSupplier?.id || '',
                startDate: dateRange?.startDate
                  ? moment(dateRange?.startDate).format('MMM DD, YYYY')
                  : '',
                endDate: dateRange?.endDate
                  ? moment(dateRange?.endDate).format('MMM DD, YYYY')
                  : '',
                timezone: validTimezone,
                buyerId,
              })
            );
          }
        }}
        reloadSelectedTransaction={() => {
          if (selectedTransaction) {
            handleShowPaidInvoices(selectedTransaction);
          }
        }}
        accountType="supplier"
        selectedTransaction={selectedTransaction}
        onViewMainPayment={handleViewMainPayment}
      />
      <AppDateRangePickerModal
        open={isAppDateRangePickerModalOpen}
        selectedDateRange={dateRange}
        handleClose={() => {
          setIsAppDateRangePickerModalOpen(false);
        }}
        onSelectDateRange={(selection) => {
          setIsAppDateRangePickerModalOpen(false);
          setDateRange(selection);
        }}
      />

      <DillTable<DillTransaction>
        columns={columns}
        data={supplierTransactionsFiltered}
        rowKey={(row) => row.id}
        sortable
        sortCycle="3state"
        defaultSort={
          sortOptions.sortBy ? { key: sortOptions.sortBy, dir: sortOptions.sortOrder } : undefined
        }
        searchClassName="h-9"
        toolbarLeft={
          <div className="flex items-center gap-2">
            <div className="flex flex-row items-center">
              <button
                onClick={() => setIsAppDateRangePickerModalOpen(true)}
                className="flex flex-row items-center GREY_200-BORDER border rounded-lg py-1.5 px-2 WHITE-BG hover:bg-slate-200">
                <Calendar className="text-slate-400 mr-2" size={22} />
                <div className="text-xs TEXT_PRIMARY-CLR">{`${
                  dateRange?.startDate ? moment(dateRange?.startDate).format('MMM DD, YYYY') : ''
                }-${
                  dateRange?.endDate ? moment(dateRange?.endDate).format('MMM DD, YYYY') : ''
                }`}</div>
              </button>
            </div>
            <div className="w-[260px]">
              <DillDropDownFilter
                placeholder="Filters"
                closeMenu={false}
                isMulti
                showSelectedItems
                multiValue={selectedFilters}
                items={filterItems}
                showMinimalDesign
                onMultiSelectChange={handleFilterChange}
                inputHeight={33}
              />
            </div>
          </div>
        }
        striped={false}
        virtualize
        rowHeight={80}
        emptyMessage="No Payments"
        showCount={false}
        resizable={true}
      />
    </div>
  );
};
